import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { Dispatch, SetStateAction } from 'react';
import { styled } from '@mui/material/styles';
import { FormProvider } from 'react-hook-form';
import { useInsightSummaryModal } from '../../hooks';
import {
  InsightSummaryStatusType,
  InsightSummaryType,
} from '../../queries/useInsightsSummaryQuery';
import InsightSummaryFormDetails from './InsightSummaryFormDetails';
import { theme } from 'config/theme';
import TimeLineIcon from 'remixicon-react/TimeLineIcon';
import { InsightStatusChip, InsightStatusText } from '../../helpers';

type InsightSummaryModalProps = {
  edit?: boolean;
  insightSummary?: InsightSummaryType;
  open: boolean;
  onUpdateCallback?: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function InsightSummaryModal({
  edit,
  insightSummary,
  open,
  onUpdateCallback,
  setOpen,
}: InsightSummaryModalProps) {
  const { isSavingDraft, insightSummaryForm, handleSave, handleSaveDraft, handleClose } =
    useInsightSummaryModal({
      edit,
      insightSummary,
      onUpdateCallback,
      setOpen,
    });
  const {
    formState: { isSubmitting },
  } = insightSummaryForm;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={'md'}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      disableEnforceFocus={true} // for enabling insert link modal in tinymce editor
      disableAutoFocus={true}
      TransitionComponent={GrowDialogTransition}
    >
      <CustomDialogTitle onClose={handleClose} id="dialog-title">
        <Title>{edit ? 'Edit Insight' : 'Add Insight'}</Title>
      </CustomDialogTitle>
      <DialogContent id="dialog-description" dividers>
        <FormProvider {...insightSummaryForm}>
          <InsightSummaryFormDetails edit={!!edit} />
        </FormProvider>
      </DialogContent>
      <DialogActions
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box>
          {insightSummary?.status === InsightSummaryStatusType.REVIEW_PENDING && (
            <InsightStatusChip className="review-pending">
              <TimeLineIcon size={20} color={theme.palette.custom.tuna} />
              <InsightStatusText>Review Pending</InsightStatusText>
            </InsightStatusChip>
          )}
        </Box>
        <Box>
          {insightSummary?.status === InsightSummaryStatusType.PUBLISHED ? (
            <Button variant="contained" disabled={isSubmitting} onClick={handleSave}>
              {isSubmitting ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : (
                'Save'
              )}
            </Button>
          ) : (
            <>
              <Button disabled={isSubmitting} onClick={handleSaveDraft} sx={{ mr: 1 }}>
                {isSavingDraft ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Save Changes'
                )}
              </Button>
              <Button variant="contained" disabled={isSubmitting} onClick={handleSave}>
                {isSubmitting && !isSavingDraft ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Publish'
                )}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default InsightSummaryModal;
